/**
 * Quote and bill of materials: options (VAT, cable waste, discount, labour hours), the price
 * sources (the list imported on this device, the wholesalers' lists kept on the server, Tablo's
 * Belgian reference prices) with the preferred one first, a preview of the lines with where each
 * price comes from, and the Excel / CSV export. The prices in use download as an Excel file to
 * edit and import again.
 */
import { useEffect, useMemo, useState } from 'react';
import { hasSurvey, localeTag, type Project } from '@elec/domain';
import type { Translate } from '@elec/i18n';
import {
  clearPriceList,
  loadPriceList,
  savePriceList,
  type Platform,
  type StoredPriceList,
} from '@elec/platform';
import type { CloudClient } from '@elec/sync';
import type * as Exporters from '@elec/export';
import {
  availableSources,
  loadServerPrices,
  ordered,
  PREFERRED_PREF,
  preferredOf,
  REFERENCE_PREF,
  useReferenceOf,
  type ServerPrices,
} from './priceSources.ts';
import { Dialog } from './ui.tsx';

type X = typeof Exporters;

export function QuoteDialog(props: {
  project: Project;
  t: Translate;
  locale: 'fr' | 'en' | 'nl';
  platform: Platform;
  baseName: string;
  load(): Promise<X>;
  save(bytes: Uint8Array, name: string, ext: string, mime: string): Promise<void>;
  /** Signed-in account: the wholesalers' price lists kept on the server. */
  client?: CloudClient | null;
  /** Start an invoice from this quote. */
  onInvoice?(quote: Exporters.Quote): void;
  onClose(): void;
}) {
  const { t, project, locale } = props;
  const [x, setX] = useState<X | null>(null);
  const [prices, setPrices] = useState<StoredPriceList | null>(null);
  const [vatPct, setVat] = useState(21);
  const [wastePct, setWaste] = useState(10);
  const [discountPct, setDiscount] = useState(0);
  const [labourHours, setLabourHours] = useState(0);
  const [useLibrary, setUseLibrary] = useState(() => useReferenceOf(props.platform));
  const [preferred, setPreferred] = useState(() => preferredOf(props.platform));
  const [server, setServer] = useState<ServerPrices | null>(null);
  const [serverDown, setServerDown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void props.load().then(setX);
    void loadPriceList().then(setPrices);
  }, []);
  useEffect(() => {
    if (!x) return;
    void loadServerPrices(x, project, props.client ?? null).then((r) => {
      setServer(r.data);
      setServerDown(r.error);
    });
  }, [x, props.client]);
  const sources = useMemo(
    () =>
      x
        ? ordered(availableSources(x, prices, server, useLibrary, t('quote.sourceReference')), preferred)
        : [],
    [x, prices, server, useLibrary, preferred, t],
  );
  const nameOf = (id: string | undefined) => sources.find((s) => s.id === id)?.name ?? '';
  const quote = useMemo(
    () =>
      x?.buildQuote(project, {
        sources: sources.map((s) => ({ id: s.id, entries: s.entries })),
        vatPct,
        cableWastePct: wastePct,
        discountPct,
        labourHours,
      }),
    [x, project, sources, vatPct, wastePct, discountPct, labourHours],
  );
  const date = (iso: string) => new Date(iso).toLocaleDateString(localeTag(locale));
  const money = (n: number) => n.toLocaleString(localeTag(locale), { style: 'currency', currency: 'EUR' });
  const importPrices = async () => {
    setError(null);
    const f = await props.platform.openFile({
      extensions: ['csv', 'txt', 'xlsx'],
      description: t('quote.priceList'),
    });
    if (!f || !x) return;
    try {
      const list = x.parsePriceList(f.name, f.bytes);
      if (!list.entries.length) return setError(t('quote.noPrices'));
      const stored = { name: f.name, importedAt: Date.now(), entries: list.entries };
      await savePriceList(stored);
      setPrices(stored);
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const downloadPrices = () => {
    if (!x) return;
    const lines = x.mergePriceLists(useLibrary ? x.BE_PRICES.entries : [], prices?.entries ?? []);
    void props.save(
      x.priceListXlsx(lines, locale),
      t('quote.priceListFile'),
      'xlsx',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    );
  };
  const num = (v: string, d: number) => {
    const n = Number(v.replace(',', '.'));
    return Number.isFinite(n) && n >= 0 ? n : d;
  };
  const surveyOnly =
    hasSurvey(project) && !Object.values(project.sheets).some((s) => s.source?.kind === 'survey');
  return (
    <Dialog
      title={t('quote.title')}
      wide
      onClose={props.onClose}
      actions={
        <>
          <button onClick={props.onClose}>{t('dialog.close')}</button>
          {props.onInvoice && (
            <button
              disabled={!quote}
              onClick={() => quote && props.onInvoice!(quote)}
              data-testid="quote-invoice"
            >
              {t('quote.invoice')}
            </button>
          )}
          <button
            disabled={!quote}
            onClick={() =>
              quote &&
              void props.save(
                new TextEncoder().encode(x!.quoteCsv(project, quote, locale)),
                `${props.baseName} - ${t('quote.file')}.csv`,
                'csv',
                'text/csv',
              )
            }
            data-testid="quote-csv"
          >
            {t('quote.csv')}
          </button>
          <button
            className="primary"
            disabled={!quote}
            onClick={() =>
              quote &&
              void props.save(
                x!.quoteXlsx(project, quote, locale),
                `${props.baseName} - ${t('quote.file')}.xlsx`,
                'xlsx',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              )
            }
            data-testid="quote-xlsx"
          >
            {t('quote.xlsx')}
          </button>
        </>
      }
    >
      <p className="muted">{t('quote.intro')}</p>
      {surveyOnly && <p className="warn">{t('quote.fromSurvey')}</p>}
      <div className="quote-prices">
        <strong>{t('quote.priceList')}</strong>
        <span data-testid="quote-pricelist">
          {prices
            ? t('quote.priceListInfo', { name: prices.name, n: prices.entries.length })
            : t('quote.noPriceList')}
        </span>
        <button onClick={() => void importPrices()} data-testid="quote-import-prices">
          {t('quote.importPrices')}
        </button>
        {prices && (
          <button
            onClick={() => {
              void clearPriceList();
              setPrices(null);
            }}
          >
            {t('quote.removePrices')}
          </button>
        )}
        <button onClick={downloadPrices} disabled={!x} data-testid="quote-download-prices">
          {t('quote.downloadPrices')}
        </button>
      </div>
      <label className="check">
        <input
          type="checkbox"
          checked={useLibrary}
          onChange={(e) => {
            setUseLibrary(e.target.checked);
            props.platform.setPref(REFERENCE_PREF, e.target.checked ? '1' : '0');
          }}
          data-testid="quote-use-library"
        />{' '}
        {t('quote.useLibrary', {
          date: x ? new Date(`${x.BE_PRICES.checked}T12:00:00`).toLocaleDateString(localeTag(locale)) : '…',
        })}
      </label>
      {sources.length > 1 && (
        <div className="field">
          <label htmlFor="q-source">{t('quote.preferredSource')}</label>
          <select
            id="q-source"
            value={sources[0]!.id}
            onChange={(e) => {
              setPreferred(e.target.value);
              props.platform.setPref(PREFERRED_PREF, e.target.value);
            }}
            data-testid="quote-source"
          >
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.server ? ` · ${t('quote.sourceUpdated', { date: date(s.server.updated) })}` : ''}
              </option>
            ))}
          </select>
          <span className="muted" data-testid="quote-source-order">
            {t('quote.sourceOrder', { list: sources.map((s) => s.name).join(' → ') })}
          </span>
        </div>
      )}
      {serverDown && <p className="muted">{t('quote.serverPricesDown')}</p>}
      {server?.sources
        .filter((s) => s.error)
        .map((s) => (
          <p className="muted" key={s.id}>
            {t('quote.sourceStale', { name: s.name, date: date(s.updated) })}
          </p>
        ))}
      {error && <p className="warn">{error}</p>}
      <div className="form-grid">
        <div className="field">
          <label htmlFor="q-vat">{t('quote.vat')}</label>
          <select
            id="q-vat"
            value={vatPct}
            onChange={(e) => setVat(Number(e.target.value))}
            data-testid="quote-vat"
          >
            <option value={21}>{t('quote.vat21')}</option>
            <option value={6}>{t('quote.vat6')}</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="q-waste">{t('quote.waste')}</label>
          <input
            id="q-waste"
            inputMode="decimal"
            value={wastePct}
            onChange={(e) => setWaste(num(e.target.value, 10))}
          />
        </div>
        <div className="field">
          <label htmlFor="q-disc">{t('quote.discount')}</label>
          <input
            id="q-disc"
            inputMode="decimal"
            value={discountPct}
            onChange={(e) => setDiscount(Math.min(100, num(e.target.value, 0)))}
          />
        </div>
        <div className="field">
          <label htmlFor="q-hours">{t('quote.labourHours')}</label>
          <input
            id="q-hours"
            inputMode="decimal"
            value={labourHours}
            onChange={(e) => setLabourHours(num(e.target.value, 0))}
            data-testid="quote-hours"
          />
        </div>
      </div>
      {quote && (
        <>
          {quote.missingLengths.length > 0 && (
            <p className="warn">
              {t('quote.missingLengths', {
                n: quote.missingLengths.length,
                list: quote.missingLengths.join(', '),
              })}
            </p>
          )}
          <div className="quote-table">
            <table>
              <thead>
                <tr>
                  <th>{t('quote.colRef')}</th>
                  <th>{t('quote.colDesc')}</th>
                  <th className="num">{t('quote.colQty')}</th>
                  <th className="num">{t('quote.colPrice')}</th>
                  <th>{t('quote.colSource')}</th>
                </tr>
              </thead>
              <tbody data-testid="quote-lines">
                {quote.lines.map((l, i) => (
                  <tr key={i} className={l.unitPrice === undefined ? 'unpriced' : ''}>
                    <td className="mono">{l.reference}</td>
                    <td>{l.description}</td>
                    <td className="num">
                      {l.quantity} {l.unit}
                    </td>
                    <td className="num">{l.unitPrice === undefined ? '—' : money(l.unitPrice)}</td>
                    <td className="muted" data-testid="quote-line-source">
                      {nameOf(l.priceFrom)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            {t('quote.lines', { n: quote.lines.length, priced: quote.priced })} ·{' '}
            <strong data-testid="quote-totals">
              {t('quote.totals', {
                sub: money(quote.subtotal),
                vat: money(quote.vat),
                total: money(quote.total),
              })}
            </strong>
          </p>
          {quote.lines.some((l) => l.priceFrom === 'reference') && (
            <p className="muted">{t('quote.libraryPrice')}</p>
          )}
        </>
      )}
    </Dialog>
  );
}
